import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Tablet, Delete, ChevronLeft, Fingerprint, Check } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import api from '../utils/api';

const PinLogin = () => {
  const [pin, setPin] = useState(['', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [savedPin, setSavedPin] = useState(null);
  const { updateUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];

  // Check for saved PIN on mount
  useEffect(() => {
    const remembered = localStorage.getItem('hsc_remember_pin');
    if (remembered) {
      setSavedPin(remembered);
      // Auto-fill PIN
      const digits = remembered.split('');
      if (digits.length === 4) {
        setPin(digits);
        // Auto-login with saved PIN
        handleSubmit(remembered, true);
      }
    } else {
      inputRefs[0].current?.focus();
    }
  }, []);

  const handlePinChange = (index, value) => {
    // Only allow single digit
    if (value.length > 1) {
      value = value.slice(-1);
    }
    
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError('');

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }

    // Auto-submit when all 4 digits entered
    if (value && index === 3 && newPin.every(d => d !== '')) {
      handleSubmit(newPin.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handleNumberClick = (num) => {
    // Find first empty input
    const emptyIndex = pin.findIndex(d => d === '');
    if (emptyIndex !== -1) {
      handlePinChange(emptyIndex, num.toString());
    }
  };

  const handleDelete = () => {
    // Find last filled input
    const lastFilledIndex = pin.reduce((last, d, i) => d !== '' ? i : last, -1);
    if (lastFilledIndex !== -1) {
      const newPin = [...pin];
      newPin[lastFilledIndex] = '';
      setPin(newPin);
      inputRefs[lastFilledIndex].current?.focus();
    }
  };

  const handleClear = () => {
    setPin(['', '', '', '']);
    setError('');
    inputRefs[0].current?.focus();
  };

  const handleForgetSavedPin = () => {
    localStorage.removeItem('hsc_remember_pin');
    setSavedPin(null);
    setPin(['', '', '', '']);
    inputRefs[0].current?.focus();
    toast({
      title: "PIN Forgotten",
      description: "You'll need to enter your PIN next time",
    });
  };

  const handleSubmit = async (pinValue, isAutoLogin = false) => {
    if (!pinValue || pinValue.length !== 4) {
      setError('Please enter 4 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/auth/pin-login', { pin: pinValue });
      const { token, user } = response.data;
      
      // Store token
      localStorage.setItem('token', token);
      
      // Remember PIN if checkbox is checked or if this was an auto-login
      if (rememberMe || isAutoLogin) {
        localStorage.setItem('hsc_remember_pin', pinValue);
      }
      
      updateUser(user);
      
      toast({
        title: "Welcome!",
        description: `Logged in as ${user.name}`,
      });

      // HSC users go to the Rooms Matrix
      navigate('/hsc');
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid PIN';
      setError(message);
      setPin(['', '', '', '']);
      
      // Clear saved PIN if it fails
      if (savedPin) {
        localStorage.removeItem('hsc_remember_pin');
        setSavedPin(null);
      }
      
      inputRefs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex flex-col items-center justify-center p-4">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl"></div>
      </div>

      {/* Back to regular login */}
      <Link 
        to="/login" 
        className="absolute top-6 left-6 flex items-center gap-2 text-white/60 hover:text-white transition-colors"
      >
        <ChevronLeft className="w-5 h-5" />
        <span>Email Login</span>
      </Link>

      {/* Main container */}
      <div className="relative z-10 w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-xl shadow-purple-500/30">
            <Fingerprint className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">HSC Quick Access</h1>
          <p className="text-white/60 text-sm">
            {savedPin ? 'Welcome back! Logging in...' : 'Enter your 4-digit PIN'}
          </p>
        </div>

        {/* PIN Display */}
        <div className="flex justify-center gap-4 mb-6">
          {pin.map((digit, index) => (
            <input
              key={index}
              ref={inputRefs[index]}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handlePinChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className={`w-16 h-20 text-center text-3xl font-bold rounded-2xl border-2 
                ${digit ? 'bg-purple-500/30 border-purple-500 text-white' : 'bg-slate-800/50 border-slate-700 text-white/50'}
                focus:outline-none focus:border-purple-400 focus:bg-purple-500/20 transition-all`}
              disabled={loading}
            />
          ))}
        </div>

        {/* Remember Me checkbox */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <button
            onClick={() => setRememberMe(!rememberMe)}
            className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${
              rememberMe 
                ? 'bg-purple-500 border-purple-500' 
                : 'bg-transparent border-slate-600 hover:border-purple-400'
            }`}
          >
            {rememberMe && <Check className="w-4 h-4 text-white" />}
          </button>
          <span className="text-white/70 text-sm">Remember me on this device</span>
        </div>

        {/* Saved PIN info */}
        {savedPin && !loading && (
          <div className="text-center mb-4">
            <button
              onClick={handleForgetSavedPin}
              className="text-purple-400 hover:text-purple-300 text-sm underline underline-offset-2"
            >
              Not you? Forget saved PIN
            </button>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="text-center mb-6">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Number pad */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
            <button
              key={num}
              onClick={() => handleNumberClick(num)}
              disabled={loading}
              className="h-16 rounded-2xl bg-slate-800/60 border border-slate-700 text-white text-2xl font-semibold 
                hover:bg-slate-700/60 hover:border-purple-500/50 active:bg-purple-500/30 transition-all disabled:opacity-50"
            >
              {num}
            </button>
          ))}
          <button
            onClick={handleClear}
            disabled={loading}
            className="h-16 rounded-2xl bg-slate-800/60 border border-slate-700 text-white/60 text-sm font-medium 
              hover:bg-slate-700/60 hover:text-white transition-all disabled:opacity-50"
          >
            Clear
          </button>
          <button
            onClick={() => handleNumberClick(0)}
            disabled={loading}
            className="h-16 rounded-2xl bg-slate-800/60 border border-slate-700 text-white text-2xl font-semibold 
              hover:bg-slate-700/60 hover:border-purple-500/50 active:bg-purple-500/30 transition-all disabled:opacity-50"
          >
            0
          </button>
          <button
            onClick={handleDelete}
            disabled={loading}
            className="h-16 rounded-2xl bg-slate-800/60 border border-slate-700 text-white/60 
              hover:bg-slate-700/60 hover:text-white transition-all disabled:opacity-50 flex items-center justify-center"
          >
            <Delete className="w-6 h-6" />
          </button>
        </div>

        {/* Loading indicator */}
        {loading && (
          <div className="text-center">
            <div className="inline-flex items-center gap-2 text-purple-400">
              <div className="w-5 h-5 border-2 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
              <span>Authenticating...</span>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-8">
          <div className="flex items-center justify-center gap-2 text-white/40 text-xs">
            <Tablet className="w-4 h-4" />
            <span>Heavenly Service Centre Quick Login</span>
          </div>
          {rememberMe && (
            <p className="text-white/30 text-xs mt-2">
              PIN will be saved securely on this device
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PinLogin;
